/**
 * Helpers for FilterRolesModal apply payload ↔ chips / initial props /
 * legacy applied-filter array / Role v2 list API query params (ER-10901).
 *
 * Values are stable lowercase snake_case end-to-end (Filter Modal Standard):
 *   - statuses:  { draft, active, retired }  (checkbox booleans; no "inactive")
 *   - archived:  do_not_show_archived (default) | show_archived
 *   - validity:  no_filter_validity (default) | is_valid | is_not_valid
 *   - positions: no_filter_positions (default) | attached | no_position_attached
 *   - inUse:     no_filter_in_use (default) | in_use | not_used
 *   - timeline:  no_filter_timeline (default) | recently_activated |
 *                recently_retired | recently_created
 *
 * Chip suppression for the Archived default is keyed on the VALUE
 * (do_not_show_archived) — never on the literal label string.
 *
 * Legacy label values ("Show", "Do not Show", "No Positions", …) — what the
 * legacy RadioInput-based modal persisted (radio items are emitted by LABEL,
 * statuses by slug) — are accepted when converting saved filters back to a
 * payload so persisted state survives the flag flip; new values are always
 * emitted once the modal is in use.
 */

export const STATUS_LABEL_BY_VALUE = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
};

export const ARCHIVED_LABEL_BY_VALUE = {
  do_not_show_archived: "Do Not Show",
  show_archived: "Show",
};

export const VALIDITY_LABEL_BY_VALUE = {
  is_valid: "Is valid",
  is_not_valid: "Is not valid",
};

/** ER-11530: the Invalid summary tile's chip reads "Invalid"; the modal radio keeps "Is not valid". */
export const VALIDITY_CHIP_LABEL_BY_VALUE = {
  ...VALIDITY_LABEL_BY_VALUE,
  is_not_valid: "Invalid",
};

export const POSITIONS_LABEL_BY_VALUE = {
  attached: "Attached",
  no_position_attached: "No position attached",
};

export const IN_USE_LABEL_BY_VALUE = {
  in_use: "In use",
  not_used: "Not used",
};

export const TIMELINE_LABEL_BY_VALUE = {
  recently_activated: "Recently Activated",
  recently_retired: "Recently Retired",
  recently_created: "Recently Created",
};

export const DEFAULT_ROLE_FILTER_PAYLOAD = Object.freeze({
  statuses: Object.freeze({ draft: true, active: true, retired: false }),
  archived: "do_not_show_archived",
  validity: "no_filter_validity",
  positions: "no_filter_positions",
  inUse: "no_filter_in_use",
  timeline: "no_filter_timeline",
});

const RADIO_DEFAULTS = {
  archived: DEFAULT_ROLE_FILTER_PAYLOAD.archived,
  validity: DEFAULT_ROLE_FILTER_PAYLOAD.validity,
  positions: DEFAULT_ROLE_FILTER_PAYLOAD.positions,
  inUse: DEFAULT_ROLE_FILTER_PAYLOAD.inUse,
  timeline: DEFAULT_ROLE_FILTER_PAYLOAD.timeline,
};

export function cloneRoleFilterPayload(payload = DEFAULT_ROLE_FILTER_PAYLOAD) {
  return {
    statuses: {
      draft: !!payload?.statuses?.draft,
      active: !!payload?.statuses?.active,
      retired: !!payload?.statuses?.retired,
    },
    archived: payload?.archived || RADIO_DEFAULTS.archived,
    validity: payload?.validity || RADIO_DEFAULTS.validity,
    positions: payload?.positions || RADIO_DEFAULTS.positions,
    inUse: payload?.inUse || RADIO_DEFAULTS.inUse,
    timeline: payload?.timeline || RADIO_DEFAULTS.timeline,
  };
}

/**
 * Applied-filter chips (display labels). Status chips always follow the checked
 * statuses (defaults Draft/Active still show). Radio chips are only produced for
 * non-default values — the Archived default is suppressed by its VALUE key.
 */
export function buildRoleFilterChips(payload = {}) {
  const chips = [];

  Object.entries(payload.statuses || {}).forEach(([key, checked]) => {
    if (checked && STATUS_LABEL_BY_VALUE[key]) {
      chips.push(STATUS_LABEL_BY_VALUE[key]);
    }
  });

  if (payload.archived === "show_archived") {
    chips.push(ARCHIVED_LABEL_BY_VALUE.show_archived);
  }

  if (payload.validity && VALIDITY_CHIP_LABEL_BY_VALUE[payload.validity]) {
    chips.push(VALIDITY_CHIP_LABEL_BY_VALUE[payload.validity]);
  }

  if (payload.positions && POSITIONS_LABEL_BY_VALUE[payload.positions]) {
    chips.push(POSITIONS_LABEL_BY_VALUE[payload.positions]);
  }

  if (payload.inUse && IN_USE_LABEL_BY_VALUE[payload.inUse]) {
    chips.push(IN_USE_LABEL_BY_VALUE[payload.inUse]);
  }

  if (payload.timeline && TIMELINE_LABEL_BY_VALUE[payload.timeline]) {
    chips.push(TIMELINE_LABEL_BY_VALUE[payload.timeline]);
  }

  return chips;
}

/**
 * Payload → legacy applied-filter array (stable value slugs).
 * Default radio values are omitted — chip suppression keyed on the value.
 *
 * The archived default is the one case a caller may opt out of: `do_not_show_archived`
 * is the modal default, so dropping it normally means "archived not filtered". A table
 * whose default view is wider than the modal default (the ER-9266 attached-roles tab
 * lists every status, archived included) cannot tell "not filtered" from an explicit
 * "Do Not Show" that way, so it passes `{ keepArchivedDefault: true }` and the user's
 * explicit choice survives to the API as `archived: false` (ER-9266).
 */
export function payloadToAppliedFilters(payload = {}, options = {}) {
  const keepArchivedDefault = options.keepArchivedDefault === true;
  const filters = [];

  Object.entries(payload.statuses || {}).forEach(([key, checked]) => {
    if (checked && STATUS_LABEL_BY_VALUE[key]) {
      filters.push(key);
    }
  });

  if (payload.archived === "show_archived") {
    filters.push("show_archived");
  } else if (keepArchivedDefault && payload.archived === "do_not_show_archived") {
    filters.push("do_not_show_archived");
  }

  ["validity", "positions", "inUse", "timeline"].forEach((radioKey) => {
    const value = payload[radioKey];
    if (value && value !== RADIO_DEFAULTS[radioKey]) {
      filters.push(value);
    }
  });

  return filters;
}

const RADIO_VALUE_BY_LEGACY_LABEL = {
  archived: {
    "Show": "show_archived",
    "Do not Show": "do_not_show_archived",
    "Do Not Show": "do_not_show_archived",
  },
  positions: {
    "No Positions": "no_position_attached",
    "no_positions": "no_position_attached",
  },
  timeline: {
    "Recently Activated": "recently_activated",
    "Recently Retired": "recently_retired",
    "Recently Created": "recently_created",
  },
};

/**
 * Legacy applied-filter array → modal payload. Accepts both the new stable
 * value slugs and the pre-standard label strings stored under readinessRolesData.
 */
export function appliedFiltersToPayload(filters = []) {
  const values = Array.isArray(filters) ? filters : [];
  const payload = cloneRoleFilterPayload(DEFAULT_ROLE_FILTER_PAYLOAD);

  values.forEach((value) => {
    const v = String(value || "").trim();
    if (!v) return;

    if (STATUS_LABEL_BY_VALUE[v]) {
      payload.statuses = { ...payload.statuses, [v]: true };
      return;
    }

    // Stable value slug ("show_archived") maps to itself; legacy labels
    // ("Show", "Do not Show") map through the label lookup table.
    const archivedLabel = ARCHIVED_LABEL_BY_VALUE[v];
    if (archivedLabel) {
      payload.archived = v;
      return;
    }
    const legacyArchived = RADIO_VALUE_BY_LEGACY_LABEL.archived[v];
    if (legacyArchived) {
      payload.archived = legacyArchived;
      return;
    }

    if (VALIDITY_LABEL_BY_VALUE[v]) {
      payload.validity = v;
      return;
    }

    if (POSITIONS_LABEL_BY_VALUE[v]) {
      payload.positions = v;
      return;
    }

    // Legacy positions labels ("No Positions", "no_positions") map to the
    // stable no_position_attached value.
    const legacyPositions = RADIO_VALUE_BY_LEGACY_LABEL.positions[v];
    if (legacyPositions) {
      payload.positions = legacyPositions;
      return;
    }

    if (IN_USE_LABEL_BY_VALUE[v]) {
      payload.inUse = v;
      return;
    }

    const timelineLabel = TIMELINE_LABEL_BY_VALUE[v];
    if (timelineLabel) {
      payload.timeline = v;
      return;
    }
    const legacyTimeline = RADIO_VALUE_BY_LEGACY_LABEL.timeline[v];
    if (legacyTimeline) {
      payload.timeline = legacyTimeline;
      return;
    }

    // Legacy "No Responsibilities" has no indicator/filter in the standard modal — dropped.
  });

  return payload;
}

const LABEL_BY_VALUE = {
  ...STATUS_LABEL_BY_VALUE,
  ...ARCHIVED_LABEL_BY_VALUE,
  ...VALIDITY_LABEL_BY_VALUE,
  ...POSITIONS_LABEL_BY_VALUE,
  ...IN_USE_LABEL_BY_VALUE,
  ...TIMELINE_LABEL_BY_VALUE,
};

/**
 * Remove one summary chip (label) and return the updated payload (immutable).
 * Unknown chip labels leave the payload unchanged.
 */
export function removeRoleFilterChip(payload, removedChip) {
  const next = cloneRoleFilterPayload(payload);
  const removed = String(removedChip || "").trim();
  if (!removed) return next;

  const statusEntry = Object.entries(STATUS_LABEL_BY_VALUE).find(([, label]) => label === removed);
  if (statusEntry) {
    const [key] = statusEntry;
    next.statuses = { ...next.statuses, [key]: false };
    return next;
  }

  if (removed === ARCHIVED_LABEL_BY_VALUE.show_archived) {
    next.archived = RADIO_DEFAULTS.archived;
    return next;
  }

  const radioKeyByValue = [
    ["validity", VALIDITY_CHIP_LABEL_BY_VALUE],
    ["positions", POSITIONS_LABEL_BY_VALUE],
    ["inUse", IN_USE_LABEL_BY_VALUE],
    ["timeline", TIMELINE_LABEL_BY_VALUE],
  ];
  for (const [radioKey, labelMap] of radioKeyByValue) {
    const entry = Object.entries(labelMap).find(([, label]) => label === removed);
    if (entry) {
      const [key] = entry;
      if (next[radioKey] === key) {
        next[radioKey] = RADIO_DEFAULTS[radioKey];
      }
      return next;
    }
  }

  return next;
}

export { LABEL_BY_VALUE };

/**
 * Map the applied-filter array (stable values from the standard modal, or the
 * legacy label strings) to Role v2 list API query params.
 *
 * status / archived / positions=no / recently_* are already supported by the
 * endpoint. validity (is_invalid) and in-use (in_use) params are the contract
 * from ER-10312 / ER-10903 and are wired ahead of the backend landing; the new
 * modal ships behind a flag, so the legacy path is untouched until then.
 */
export function convertRoleFiltersToApiFormat(filters = []) {
  const apiFilters = {};
  const values = Array.isArray(filters) ? filters : [];

  const statusFilters = values.filter((f) => ["draft", "active", "retired", "inactive"].includes(f));
  if (statusFilters.length > 0) {
    apiFilters.status = statusFilters;
  }

  if (values.includes("show_archived") || values.includes("Show")) {
    apiFilters.archived = true;
  } else if (
    values.includes("do_not_show_archived") ||
    values.includes("Do not Show") ||
    values.includes("Do Not Show")
  ) {
    apiFilters.archived = false;
  }

  if (
    values.includes("no_position_attached") ||
    values.includes("No Positions") ||
    values.includes("no_positions")
  ) {
    apiFilters.positions = "no";
  }
  if (values.includes("attached")) {
    apiFilters.positions = "yes";
  }

  if (values.includes("is_valid")) {
    apiFilters.is_invalid = false;
  }
  if (values.includes("is_not_valid")) {
    apiFilters.is_invalid = true;
  }

  if (values.includes("in_use")) {
    apiFilters.in_use = true;
  }
  if (values.includes("not_used")) {
    apiFilters.in_use = false;
  }

  if (values.includes("recently_activated") || values.includes("Recently Activated")) {
    apiFilters.recently_activated = true;
  }
  if (values.includes("recently_retired") || values.includes("Recently Retired")) {
    apiFilters.recently_retired = true;
  }
  if (values.includes("recently_created") || values.includes("Recently Created")) {
    apiFilters.recently_created = true;
  }

  return apiFilters;
}
